import React, { useState, useMemo, useEffect, useRef } from 'react';
import dayjs from 'dayjs';
import {
    Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Tooltip,Legend
} from 'recharts';
import { Box, Typography,Select,MenuItem,Checkbox,ListItemText} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import Menu from '@mui/material/Menu';

import IconButton from '@mui/material/IconButton';
import { saveAs } from 'file-saver'; // For PNG/SVG download
import * as htmlToImage from 'html-to-image';
import axios from 'axios';
import configParam from '../../config';

import html2canvas from "html2canvas";
// Custom Dot Components
const CustomDot1 = ({ cx, cy, value }) => (
    <g>
        <circle cx={cx} cy={cy} r={3} fill="#1890FF" strokeWidth={2} />
        <text x={cx} y={cy - 10} fontSize={12} textAnchor="middle" fill="#17428B">
            {value}
        </text>
    </g>
);

const CustomDot2 = ({ cx, cy, value }) => (
    <g>
        <circle cx={cx} cy={cy} r={3} fill="#17428B" strokeWidth={2} />
        <text x={cx} y={cy - 10} fontSize={12} textAnchor="middle" fill="#17428B">
            {value}
        </text>
    </g>
);

// Custom components for ticks (Radius and Angle)
const CustomAngleTick = ({ payload, x, y, textAnchor }) => {
    const subject = payload.value;
    return (
        <g transform={`translate(${x},${y})`}>
            <text
                textAnchor={textAnchor}
                fill="#17428B"
                fontSize={10}
                fontWeight={500}
                dy={0}
                dx={0}
            >
                {subject}
            </text>
        </g>
    );
};

const CustomRadiusTick = ({ payload, x, y }) => {
    if (payload.value === 0) return null;
    const textWidth = 24;
    const textHeight = 18;
    return (
        <g>
            <rect
                x={x - textWidth / 2}
                y={y + 3}
                width={textWidth}
                height={textHeight}
                fill="#E7E7E7"
                rx={4}
            />
            <text
                x={x}
                y={y}
                fill="#17428B"
                fontSize={8}
                fontWeight={500}
                textAnchor="middle"
                dy={16}
            >
                {payload.value}
            </text>
        </g>
    );
};
//new code

const RadarChartCoordinator = ({
  totalScoresRadarCoordinator,
  selectedYearRadar,
  setSelectedYearRadar,
  selectedMonthsRadar,
  setSelectedMonthsRadar,
  years,
}) => {
  const months = Array.from({ length: 12 }, (_, i) => ({
    value: i + 1,
    label: dayjs().month(i).format("MMMM"),
  }));
const chartRef = useRef(null); // Ref for capturing the chart as an image
  const [menuAnchorEl, setMenuAnchorEl] = React.useState(null); 
  const uniqueGroupNames = [...new Set(totalScoresRadarCoordinator.map((item) => item.groupName))];

  // Dynamically generate colors for each month
  const colorPalette = [
    "#FF5733", "#33FF57", "#3357FF", "#FF33A1", "#A133FF", "#33FFF1",
    "#FFC133", "#8DFF33", "#FF338F", "#33FF8D", "#8D33FF", "#FF8D33",
  ];

  const radarData = useMemo(() => {
    return uniqueGroupNames.map((groupName) => {
      const groupScores = {};
      totalScoresRadarCoordinator.forEach((item) => {
        if (item.groupName === groupName) {
          groupScores[`month${item.audit_month}`] = parseFloat(item.averageScore);
        }
      });
      return {
        subject: groupName,
        ...groupScores,
        fullMark: 40,
      };
    });
  }, [totalScoresRadarCoordinator, uniqueGroupNames]);

  const [highScoreCount, lowScoreCount, equalScoreCount] = useMemo(() => {
    if (selectedMonthsRadar.length < 2) return [0, 0, 0]; // Ensure at least two months are selected

    const [prevMonth, latestMonth] = selectedMonthsRadar.slice(-2);

    let highScoreGroups = 0;
    let lowScoreGroups = 0;
    let equalScoreGroups = 0;

    radarData.forEach(group => {
        const prevScore = group[`month${prevMonth}`] || 0;
        const latestScore = group[`month${latestMonth}`] || 0;

        if (latestScore > prevScore) highScoreGroups += 1;
        else if (latestScore < prevScore) lowScoreGroups += 1;
        else equalScoreGroups += 1;
    });

    return [highScoreGroups, lowScoreGroups, equalScoreGroups];
}, [radarData, selectedMonthsRadar]);

const CustomLegend = () => {
    return (
        <div style={{ display: 'flex', fontSize: '12px', color: '#17428B', marginBottom: '10px', justifyContent: 'space-evenly' }}>
            <p><strong>High Score Groups:</strong> {highScoreCount}</p>
            <p><strong>Low Score Groups:</strong> {lowScoreCount}</p>
            <p><strong>Equal Score Groups:</strong> {equalScoreCount}</p>
        </div>
    );
};

// Inside RadarChart Component



  const handleMonthChange = (event) => {
    const value = event.target.value;
    setSelectedMonthsRadar(
      typeof value === "string" ? value.split(",").map(Number) : value
    );
  };
  const downloadCSV = () => {
    const csvContent = [
      ["Subject", ...selectedMonthsRadar.map(month => dayjs().month(month - 1).format("MMMM"))],
      ...radarData.map(data => [
        data.subject,
        ...selectedMonthsRadar.map(month => data[`month${month}`] || "")
      ])
    ]
      .map(row => row.join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    saveAs(blob, "radar_chart_data.csv");
  };

  const downloadImage = async (type) => {
    const chartElement = document.querySelector('.recharts-wrapper'); // Select chart element
    if (chartElement) {
        const blob = await (type === 'png'
            ? htmlToImage.toBlob(chartElement)
            : htmlToImage.toSvg(chartElement));
        saveAs(blob, `radar_chart.${type}`);
    }
    handleMenuClose();
};
  const handleMenuClick = (event) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };
  return (
     <Box sx={{ border: '1px solid #ccc', borderRadius: '5px', width: '100%' }}>
                <Box sx={{ borderBottom: '1px solid #ccc', padding: '12px', display: {
              md:'flex', xs: "block"
             }, justifyContent: 'space-between', alignItems: 'center' }}>
     <Typography variant='h4' sx={{ color: '#17428B', fontSize: '16px', fontWeight: '600', marginBottom: {md:'0',xs:'12px'} }}>6S Audit Summary - India</Typography>
      {/* <div style={{ display: "flex", gap: "10px", marginBottom: "20px" }}>
       */}
       <Box
        sx={{
          display: "flex",
          gap: "8px",
          alignItems: "center",
          flexWrap: "wrap",
          justifyContent: { xs: "flex-end", md: "flex-start" }, // Align right on mobile
        }}
      >
        <Select
          value={selectedYearRadar}
          onChange={(e) => setSelectedYearRadar(Number(e.target.value))}
          sx={{
            height: "32px",
            fontSize: "14px",
            padding: "4px 0px",
            minWidth: "75px",
            width: "auto",
            flexShrink: 0, // Prevents shrinking
          }}
        >
          {years.map((year) => (
            <MenuItem key={year} value={year}>
              {year}
            </MenuItem>
          ))}
        </Select>

        <Select
          multiple
          value={selectedMonthsRadar}
          onChange={handleMonthChange}
          renderValue={(selected) =>
            selected.map((month) => dayjs().month(month - 1).format("MMMM")).join(", ")
          }
          displayEmpty
          sx={{
            height: "33px",
            fontSize: "14px",
            padding: "4px 0px",
            minWidth: "100px", // Set a fixed width
            maxWidth: "120px", // Prevents expansion
            width: "120px",
            flexShrink: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {months.map((month) => (
            <MenuItem key={month.value} value={month.value}>
              <Checkbox checked={selectedMonthsRadar.includes(month.value)} />
              <ListItemText primary={month.label} />
            </MenuItem>
          ))}
        </Select>

        <IconButton onClick={handleMenuClick}>
            <MoreVertIcon />
          </IconButton>
          <Menu anchorEl={menuAnchorEl} open={Boolean(menuAnchorEl)} onClose={handleMenuClose}>
          <MenuItem onClick={downloadCSV}>Download as CSV</MenuItem>
          <MenuItem onClick={() => downloadImage('png')}>Download as PNG</MenuItem>
          <MenuItem onClick={() => downloadImage('svg')}>Download as SVG</MenuItem>
          </Menu>
      {/* </div> */}
      </Box>
      </Box>
      <Box
        sx={{
          width: "100%",
          overflow: "hidden",
          display: "flex",
          justifyContent: "center",
        }}
      >
        <RadarChart outerRadius={270} width={900} height={700} data={radarData}>
          <PolarGrid />
          <PolarAngleAxis dataKey="subject" />
          <PolarRadiusAxis angle={90} domain={[0, 40]} />
          {selectedMonthsRadar.map((month, index) => (
            <Radar
              key={month}
              name={dayjs().month(month - 1).format("MMMM")}
              dataKey={`month${month}`}
              stroke={colorPalette[index % colorPalette.length]}
              fill={colorPalette[index % colorPalette.length]}
              fillOpacity={0.3}
            />
          ))}
          <Tooltip />
          <Legend content={<CustomLegend />} />
        </RadarChart>
      </Box>
    </Box>
  );
};

export default RadarChartCoordinator;


//prod code
// const months = Array.from({ length: 12 }, (_, i) => ({
//     value: i + 1,
//     label: dayjs().month(i).format('MMMM')
// }));

// const RadarChartCoordinator = ({ totalScoresRadarCoordinator }) => {
//     console.log("totalScoresRadarCoordinator",totalScoresRadarCoordinator)
//     const currentYear = dayjs().year();
//     const currentMonth = dayjs().month() + 1;

//     const [selectedYear, setSelectedYear] = useState(currentYear);
//     const [selectedMonths, setSelectedMonths] = useState([currentMonth]);

//     const [anchorEl, setAnchorEl] = useState(null);
//     const isMenuOpen = Boolean(anchorEl);

//     const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
//     const handleMenuClose = () => setAnchorEl(null);

//     const downloadCSV = () => {
//         const csvHeader = 'Group Name,' + selectedMonths.map(month => dayjs().month(month - 1).format('MMMM')).join(',') + '\n';
//         const csvBody = radarData.map(item => {
//             const scores = selectedMonths.map(month => item[`month${month}`] || 0).join(',');
//             return `${item.subject},${scores}`;
//         }).join('\n');
//         const csvContent = csvHeader + csvBody;

//         const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
//         saveAs(blob, 'radar_chart_data.csv');
//         handleMenuClose();
//     };

//     const downloadImage = async (type) => {
//         const chartElement = document.querySelector('.recharts-wrapper'); // Select chart element
//         if (chartElement) {
//             const blob = await (type === 'png'
//                 ? htmlToImage.toBlob(chartElement)
//                 : htmlToImage.toSvg(chartElement));
//             saveAs(blob, `radar_chart.${type}`);
//         }
//         handleMenuClose();
//     };


//     // Create a unique list of group names
//     const uniqueGroupNames = [...new Set(totalScoresRadarCoordinator.map(item => item.groupName))];

//     // Filter data based on the selected year and months
//     const filteredData = useMemo(() => {
//         return totalScoresRadarCoordinator.filter(item => {
//             const itemYear = parseInt(item.audit_year, 10);
//             const itemMonth = parseInt(item.audit_month, 10);
//             return (
//                 itemYear === selectedYear &&
//                 selectedMonths.includes(itemMonth)
//             );
//         });
//     }, [totalScoresRadarCoordinator, selectedYear, selectedMonths]);

//     // Organize data for radar chart based on unique group names and selected months
//     const radarData = useMemo(() => {
//         return uniqueGroupNames.map(groupName => {
//             const groupScores = {};
//             selectedMonths.forEach(month => {
//                 const groupData = filteredData.find(
//                     item => item.groupName === groupName && parseInt(item.audit_month, 10) === month
//                 );
//                 groupScores[`month${month}`] = groupData ? groupData.averageScore : 0;
//             });
//             return {
//                 subject: groupName,
//                 ...groupScores,
//                 fullMark: 40
//             };
//         });
//     }, [uniqueGroupNames, filteredData, selectedMonths]);

//     // Custom legend to display scores count
//     const [highScoreCount, lowScoreCount, equalScoreCount] = useMemo(() => {
//         if (selectedMonths.length < 2) return [0, 0, 0];

//         // Sort selected months in ascending order and take the last two for comparison
//         const [prevMonth, latestMonth] = selectedMonths.slice(-2);

//         let highScoreGroups = 0;
//         let lowScoreGroups = 0;
//         let equalScoreGroups = 0;

//         radarData.forEach(group => {
//             const prevScore = group[`month${prevMonth}`] || 0;
//             const latestScore = group[`month${latestMonth}`] || 0;

//             if (latestScore > prevScore) highScoreGroups += 1;
//             else if (latestScore < prevScore) lowScoreGroups += 1;
//             else equalScoreGroups += 1;
//         });

//         return [highScoreGroups, lowScoreGroups, equalScoreGroups];
//     }, [radarData, selectedMonths]);

//     const CustomLegend = () => (
//         <div style={{ display: 'flex', fontSize: '12px', color: '#17428B', marginBottom: '10px', justifyContent: 'space-evenly' }}>
//             <p><strong>High Score Groups:</strong> {highScoreCount}</p>
//             <p><strong>Low Score Groups:</strong> {lowScoreCount}</p>
//             <p><strong>Equal Score Groups:</strong> {equalScoreCount}</p>
//         </div>
//     );

//     useEffect(() => {
//         const lastPath = document.querySelector('.recharts-polar-grid-concentric path:last-child');
//         if (lastPath) {
//             lastPath.style.stroke = '#FEC401';
//         }
//     }, []);

//     const handleMonthChange = (event) => {
//         const value = event.target.value;
//         setSelectedMonths(typeof value === 'string' ? value.split(',') : value);
//     };

//     return (
//         <Box sx={{ border: '1px solid #ccc', borderRadius: '5px', width: '100%' }}>
//             <Box sx={{ borderBottom: '1px solid #ccc', padding: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
//                 <Typography variant='h4' sx={{ color: '#17428B', fontSize: '16px', fontWeight: '600' }}>5S Audit Summary - India</Typography>
//                 <div style={{ display: 'flex', gap: '10px' }}>
//                     {/* Year Selector */}
//                     <select
//                         value={selectedYear}
//                         onChange={e => setSelectedYear(Number(e.target.value))}
//                     >
//                         {[2020, 2021, 2022, 2023, 2024].map(year => (
//                             <option key={year} value={year}>{year}</option>
//                         ))}
//                     </select>

//                     {/* Month Multi-Select Dropdown using Material-UI */}
//                     <Select
//                         multiple
//                         value={selectedMonths}
//                         onChange={handleMonthChange}
//                         renderValue={(selected) => selected.map(month => dayjs().month(month - 1).format('MMMM')).join(', ')}
//                         displayEmpty
//                     >
//                         {months.map((month) => (
//                             <MenuItem key={month.value} value={month.value}>
//                                 <Checkbox checked={selectedMonths.indexOf(month.value) > -1} />
//                                 <ListItemText primary={month.label} />
//                             </MenuItem>
//                         ))}
//                     </Select>


//                     <IconButton onClick={handleMenuOpen}>
//                         <MoreVertIcon />
//                     </IconButton>
//                     <Menu
//                         anchorEl={anchorEl}
//                         open={isMenuOpen}
//                         onClose={handleMenuClose}
//                     >
//                         <MenuItem onClick={downloadCSV}>Download as CSV</MenuItem>
//                         <MenuItem onClick={() => downloadImage('png')}>Download as PNG</MenuItem>
//                         <MenuItem onClick={() => downloadImage('svg')}>Download as SVG</MenuItem>
//                     </Menu>
//                 </div>
//             </Box>

//             <Box sx={{ width: '100%', overflow: 'hidden', display: "flex", justifyContent: 'center' }}>
//                 <RadarChart outerRadius={270} width={900} height={700} data={radarData}>
//                     <PolarGrid className="polar-grid" stroke="#d8d8d8" />
//                     <PolarAngleAxis dataKey="subject" tick={{ fontSize: 12 }} />
//                     <PolarRadiusAxis angle={90}
//                       domain={[0, 40]}
//                       tickValues={[0, 15, 20, 25, 30, 35, 40]}
//                       stroke="#ccc"
//                       tickCount={7}
//                     //  stroke="#ccc" 
//                     />

//                     {/* Plot radar for each selected month with distinct colors */}
//                     {selectedMonths.map((month, index) => (
//                         <Radar
//                             key={month}
//                             name={dayjs().month(month - 1).format('MMMM')}
//                             dataKey={`month${month}`}
//                             stroke={['#1890FF', '#17428B', '#F56B00'][index % 3]}
//                             fill={['#17428b2e', '#1890ff24', '#f56b0030'][index % 3]}
//                             fillOpacity={0.2}
//                         />
//                     ))}

// <Tooltip
//     content={({ payload }) => {
//         if (payload && payload.length) {
//             const { subject, ...monthScores } = payload[0].payload;
//             return (
//                 <div style={{ backgroundColor: 'rgba(255, 255, 255, 0.8)', padding: '5px', borderRadius: '5px' }}>
//                     <strong>{subject}</strong>
//                     {Object.entries(monthScores)
//                         .filter(([monthKey]) => monthKey.startsWith('month'))
//                         .map(([monthKey, score]) => (
//                             <p key={monthKey}>
//                                 {dayjs().month(Number(monthKey.replace('month', '')) - 1).format('MMMM')}: {score}
//                             </p>
//                         ))}
//                 </div>
//             );
//         }
//         return null;
//     }}
// />


                    

//                     <Legend content={<CustomLegend />} />
//                 </RadarChart>
//             </Box>
//         </Box>
//     );
// };

// export default RadarChartCoordinator;
